import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Switch,
  Tooltip,
  Select,
  Skeleton,
  FormInstance,
  FormProps
} from 'antd';
import { LinkOutlined } from '@ant-design/icons';
import SortableMultiSelect from '../common/sortablemultiselect';
import FormButton from '../common/formbutton';
import CancelButton from '../common/cancelbutton';
import SortableFormList from '../common/sortableformlist';
import SignatureSwitch from './signatureswitch';
import { getMenuOptionGroups } from '../../services/menuoptiongroupservice';
import { getAllMenuAddOnGroups } from '../../services/menuaddonservice';
import { getMenuTraits } from '../../services/pageservice';

interface MenuItemFormProps {
  form: FormInstance;
  onCancel?: () => void;
  onSave?: (values: any) => void;
  onValuesChange?: FormProps['onValuesChange'];
  menuItemGuid: string;
}

interface OptionType {
  value: string;
  label: string;
}

const MenuItemForm: React.FC<MenuItemFormProps> = ({
  form,
  onCancel,
  onSave,
  onValuesChange,
  menuItemGuid
}) => {
  const [menuOptionGroups, setMenuOptionGroups] = useState<OptionType[]>([]);
  const [addOnGroups, setAddOnGroups] = useState<OptionType[]>([]);
  const [traits, setTraits] = useState<OptionType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const signatureItem = Form.useWatch("signatureItem", form);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const fetchMenuOptionGroups = async () => {
          const data = await getMenuOptionGroups();
          const menuOptions = data.map((item: any) => ({
            value: item.guid,
            label: item.name
          }));
          
          setMenuOptionGroups(menuOptions);
        };

        const fetchAvailableAddOnGroups = async () => {
          const data = await getAllMenuAddOnGroups();
          const addOns = data.map((item: any) => ({
            value: item.guid,
            label: item.name
          }));
          setAddOnGroups(addOns);
        };

        const fetchAllMenuTraits = async () => {
          const data = await getMenuTraits();
          const traits = data.map((item: any) => ({
            value: item.value,
            label: item.label
          }));
          setTraits(traits);
        };

        await Promise.all([
          fetchMenuOptionGroups(),
          fetchAvailableAddOnGroups(),
          fetchAllMenuTraits()
        ]);
      } catch (error) {
        console.error("Error fetching MenuOption and AddOns groups:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const kdsLocationOptions: OptionType[] = [
    { name: "Drinks", value: "Drinks" },
    { name: "Expo", value: "Expo" },
    { name: "Fryer", value: "Fryer" },
    { name: "Subs", value: "Subs" }
  ].map(({ name, value }) => ({ label: name, value }));

  return (
    <>
      {loading ? (
        <Skeleton active />
      ) : (
        <Form
          form={form}
          colon
          layout="vertical"
          onValuesChange={onValuesChange}
        >
          <Tooltip title={form.getFieldValue('imageName')} placement="bottomRight">
            <Form.Item name="imageName" label="Image Url">
              <Input prefix={<LinkOutlined />} />
            </Form.Item>
          </Tooltip>

          <Form.Item label="Menu Option Groups" name="menuOptionGroups">
            <SortableMultiSelect
              options={menuOptionGroups}
              placeholder="Select your menuoptions"
              style={{ width: '100%' }}
              disabled={signatureItem === true}
            />
          </Form.Item>

          <Form.Item label="KDS Locations" name="kdsLocations">
            <SortableMultiSelect
              options={kdsLocationOptions}
              placeholder="Select KDS Locations"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item label="Add-On Groups" name="addOnGroups">
            <SortableMultiSelect
              options={addOnGroups}
              placeholder="Select your addons"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item label="Traits" name="traits">
            <Select
              mode='multiple'
              options={traits}
              placeholder="Select your traits"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            label="KDS Name"
            name="kdsName"
          >
            <Input placeholder="Enter KDS Name" />
          </Form.Item>

          <SortableFormList
            name="kdsInstructions"
            label="KDS Instructions"
            placeholder="Enter instruction (max 30 chars)"
            form={form}
            alternateName="Instruction"
            maxLength={30}
            tooltipText="Add step-by-step instructions for the kitchen display system. Each instruction is capped at 30 characters."
            extra="These instructions will display below the Recipe on KDS"
          />

          <Form.Item label="Signature Item" name="signatureItem" valuePropName="checked">
            <SignatureSwitch
              form={form}
              menuItemGuid={menuItemGuid}
            />
          </Form.Item>

          <SortableFormList
            name="highlights"
            label="Highlights"
            placeholder="Enter Highlight name"
            form={form}
            alternateName="Highlight"
            maxLength={10}
            maxCount={7}
            tooltipText="Use Highlights to showcase key features of a menu item. Max 3 highlights allowed."
          />

          <Form.Item
            name="tag"
            label="Tag"
            hidden
          >
            <Input placeholder="Enter tag" maxLength={50} />
          </Form.Item>

          <div style={{ textAlign: 'right' }}>
            <CancelButton onClick={() => onCancel?.()} />
            <FormButton title={"Save"} onClick={() => onSave?.(form.getFieldsValue())} />
          </div>
        </Form>
      )}
    </>
  );
};

export default MenuItemForm;